import { EntityType } from '@frontend/common';
import { Application, Feature } from '@frontend/functionality/types';
import { getApplicationDisplayName } from '@frontend/functionality/utils';
import { CommonMessage } from '@frontend/lang';
import { userRequest } from '@frontend/user/events';
import { IUser } from '@frontend/user/types';
import { User } from '@frontend/user/utils';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { ReactiveDetailRegistryEntry, ReactiveDetailView } from '../../modal/reactive-detail-shell/reactive-detail.types';
import {
    ContactGeneralSection,
    UserActivityLaundrySection,
    UserActivityLendingSection,
    UserActivityPudoSection,
    UserActivityVendingSection,
    UserBudgetsSection,
    UserDetailsCard,
    UserEventsSection,
    UserGeneralSection,
    UserItemsSection,
    UserRolesAndLicensesSection,
    UserSecuritySection
} from './user-detail-sections';

export function isContactDetailContext(context: Record<string, string>): boolean {
    return context.application === Application.ADDRESS_BOOK || context.feature === Feature.CONTACTS;
}

const UserReactiveTab = ({
    view,
    tab
}: {
    view: ReactiveDetailView<IUser>;
    tab:
        | 'general'
        | 'items'
        | 'roles-and-licenses'
        | 'security'
        | 'budgets'
        | 'activity-pudo'
        | 'activity-laundry'
        | 'activity-vending'
        | 'activity-lending'
        | 'events';
}) => {
    const user = view.object;
    const contact = isContactDetailContext(view.context);

    if (tab === 'general') {
        return (
            <>
                <UserDetailsCard
                    user={user}
                    contact={contact}
                />
                {contact ? <ContactGeneralSection user={user} /> : <UserGeneralSection user={user} />}
            </>
        );
    }
    if (contact) return null;
    if (tab === 'items') return <UserItemsSection user={user} />;
    if (tab === 'roles-and-licenses') return <UserRolesAndLicensesSection user={user} />;
    if (tab === 'security') return <UserSecuritySection user={user} />;
    if (tab === 'budgets') return <UserBudgetsSection user={user} />;
    if (tab === 'activity-pudo') return <UserActivityPudoSection user={user} />;
    if (tab === 'activity-laundry') return <UserActivityLaundrySection user={user} />;
    if (tab === 'activity-vending') return <UserActivityVendingSection user={user} />;
    if (tab === 'activity-lending') return <UserActivityLendingSection user={user} />;
    if (tab === 'events') return <UserEventsSection user={user} />;
    return null;
};

export const userReactiveDetail: ReactiveDetailRegistryEntry<IUser> = {
    entityType: EntityType.USER,
    resolveFunction: userRequest,
    title: ({ object, context }) =>
        isContactDetailContext(context) ? (
            <div className='flex gap-2'>
                <span>{CommonMessage.OBJECTS.CONTACT.DEFAULT}:</span>
                <span>{`${object.first_name ?? ''} ${object.last_name ?? ''}`}</span>
            </div>
        ) : (
            <>
                {CommonMessage.OBJECTS.USER.DEFAULT}: {new User(object).getDisplayName()}
            </>
        ),
    tabs: [
        {
            id: 'general',
            label: CommonMessage.DETAIL.GENERAL,
            render: (view) => (
                <UserReactiveTab
                    view={view}
                    tab='general'
                />
            )
        },
        {
            id: 'activity',
            label: (
                <FormattedMessage
                    id='platform.user.modal.user.reactive-detail.activity-tab'
                    description='Tab label for user activity (transactions) on the user detail pane'
                    defaultMessage='Activity'
                />
            ),
            visible: (view) => !isContactDetailContext(view.context),
            dropdownOptions: [
                {
                    id: 'pudo',
                    label: getApplicationDisplayName(Application.PUDO),
                    render: (view) => (
                        <UserReactiveTab
                            view={view}
                            tab='activity-pudo'
                        />
                    )
                },
                {
                    id: 'laundry',
                    label: getApplicationDisplayName(Application.LAUNDRY),
                    render: (view) => (
                        <UserReactiveTab
                            view={view}
                            tab='activity-laundry'
                        />
                    )
                },
                {
                    id: 'vending',
                    label: getApplicationDisplayName(Application.VENDING),
                    render: (view) => (
                        <UserReactiveTab
                            view={view}
                            tab='activity-vending'
                        />
                    )
                },
                {
                    id: 'lending',
                    label: getApplicationDisplayName(Application.LENDING),
                    render: (view) => (
                        <UserReactiveTab
                            view={view}
                            tab='activity-lending'
                        />
                    )
                }
            ]
        },
        {
            id: 'items',
            label: (
                <FormattedMessage
                    id='platform.user.modal.user.reactive-detail.items-tab'
                    description='Tab label for user items on the user detail pane'
                    defaultMessage='Items'
                />
            ),
            visible: (view) => !isContactDetailContext(view.context),
            render: (view) => (
                <UserReactiveTab
                    view={view}
                    tab='items'
                />
            )
        },
        {
            id: 'roles-and-licenses',
            label: (
                <FormattedMessage
                    id='user.roles-and-licenses'
                    defaultMessage='Roles & licenses'
                />
            ),
            visible: (view) => !isContactDetailContext(view.context),
            render: (view) => (
                <UserReactiveTab
                    view={view}
                    tab='roles-and-licenses'
                />
            )
        },
        {
            id: 'security',
            label: (
                <FormattedMessage
                    id='user.modal.tabs.security-and-sign-in'
                    defaultMessage='Security & sign-in'
                    description='Security and sign-in tab label in user detail modal'
                />
            ),
            visible: (view) => !isContactDetailContext(view.context),
            render: (view) => (
                <UserReactiveTab
                    view={view}
                    tab='security'
                />
            )
        },
        {
            id: 'budgets',
            label: CommonMessage.OBJECTS.BUDGET.PLURAL,
            visible: (view) => !isContactDetailContext(view.context),
            render: (view) => (
                <UserReactiveTab
                    view={view}
                    tab='budgets'
                />
            )
        },
        {
            id: 'events',
            label: CommonMessage.DETAIL.EVENTS,
            visible: (view) => !isContactDetailContext(view.context),
            render: (view) => (
                <UserReactiveTab
                    view={view}
                    tab='events'
                />
            )
        }
    ]
};
